import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Modal, InlineSpinner } from './UI';
import { SignatureInput, TaeppTitle } from './TaeppUI';

// "Review and Sign" step shown before a trainer's claim is created. The PDF preview is
// the real claim statement (rendered by the server from `previewBody`, nothing saved)
// with the trainer's bank details and signature already in place, and both can still be
// changed here. Sign and Submit is the only thing that files the claim: onSubmit gets
// { signature, bank_name, bank_account_number } (signature is a data URL, or undefined to
// file the saved one). The claim keeps its own copy of both, so changing them on a later
// claim never alters one already submitted. Styles: `.taepp .sgn-*` in index.css.
//
// previewBody: { session_ids: [...] } or { manual: { fee_type, claim_date, pax } }

const ACCOUNT_FORMAT = /^[0-9][0-9 -]{3,62}$/;

const errorMessage = async err => {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).message; } catch { /* not JSON */ }
  }
  return data?.message || 'Failed to load the preview';
};

// `area` places the step in the grid (desktop: steps left, statement right; mobile: one column in order).
function Step({ n, title, area, last, children }) {
  return (
    <section className={`sgn-step sgn-a-${area}${last ? ' last' : ''}`}>
      <span className="sgn-num" aria-hidden="true">{n}</span>
      <div className="sgn-step-body">
        <h3 className="sgn-step-t">{title}</h3>
        {children}
      </div>
    </section>
  );
}

export default function SignClaimModal({ show, onClose, title, subtitle, previewBody, submitting, onSubmit }) {
  const [saved, setSaved] = useState(null);             // null until loaded
  const [signature, setSignature] = useState(null);     // data URL, '' = use saved, null = nothing yet
  const [bank, setBank] = useState({ name: '', account: '' });
  const [bankShown, setBankShown] = useState({ name: '', account: '' });   // what the preview is drawn with
  const [confirmed, setConfirmed] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    if (!show) return;
    setSaved(null);
    setSignature(null);
    setConfirmed(false);
    setPreviewError('');
    api.get('/trainer/claim-details')
      .then(r => {
        const d = r.data.data;
        setSaved(d);
        setBank({ name: d.bank_name || '', account: d.bank_account_number || '' });
        setBankShown({ name: d.bank_name || '', account: d.bank_account_number || '' });
      })
      .catch(() => setSaved({ has_signature: false, image: null }));
  }, [show]);

  // The statement follows the bank fields once typing pauses.
  useEffect(() => {
    const t = setTimeout(() => setBankShown(bank), 500);
    return () => clearTimeout(t);
  }, [bank]);

  // Re-render the statement whenever what is printed on it changes.
  const bodyKey = JSON.stringify(previewBody || {});
  useEffect(() => {
    if (!show || !saved) return undefined;
    let stale = false;
    setPreviewing(true);
    setPreviewError('');
    api.post('/trainer/claims/preview', {
      ...previewBody, signature: signature || undefined, bank_name: bankShown.name, bank_account_number: bankShown.account,
    }, { responseType: 'blob' })
      .then(res => {
        if (stale) return;
        setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
      })
      .catch(async err => { const message = await errorMessage(err); if (!stale) setPreviewError(message); })
      .finally(() => { if (!stale) setPreviewing(false); });
    return () => { stale = true; };
  }, [show, saved, signature, bankShown, bodyKey]);

  useEffect(() => {
    if (show) return undefined;
    setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
    return undefined;
  }, [show]);

  const account = bank.account.trim();
  const accountInvalid = !!account && !ACCOUNT_FORMAT.test(account);
  const hasSignature = signature === '' ? !!saved?.has_signature : !!signature;
  const canSubmit = hasSignature && confirmed && !accountInvalid && !previewError && !submitting;
  const missing = accountInvalid ? 'Fix the bank account number'
    : !hasSignature ? 'Add your signature'
    : !confirmed ? 'Tick the box to confirm' : '';

  return (
    <Modal show={show} onClose={() => !submitting && onClose()} size="xl"
      title={<TaeppTitle icon="bi-pen" title={title} subtitle={subtitle} />}>
      <div className="taepp sgn-grid">
        <Step n="1" title="Bank Details" area="bank">
          {saved ? (
            <div className="sgn-fields">
              <div>
                <label htmlFor="sgn-bank-name" className="tp-hint sgn-field-l">Bank Name</label>
                <input id="sgn-bank-name" type="text" className="sms-form-control" maxLength={100} autoComplete="off"
                  value={bank.name} onChange={e => setBank(b => ({ ...b, name: e.target.value }))} placeholder="e.g. DBS" />
              </div>
              <div>
                <label htmlFor="sgn-bank-account" className="tp-hint sgn-field-l">Bank Account Number</label>
                <input id="sgn-bank-account" type="text" inputMode="numeric" className="sms-form-control" maxLength={64} autoComplete="off"
                  aria-invalid={accountInvalid} value={bank.account}
                  onChange={e => setBank(b => ({ ...b, account: e.target.value }))} placeholder="e.g. 123-456-789" />
                {accountInvalid && <div className="tp-error"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>Use digits only. Spaces and dashes are allowed.</span></div>}
              </div>
              <p className="tp-hint" style={{ margin: 0 }}>Printed on this claim only. Changing them later won't change claims you've already submitted.</p>
            </div>
          ) : (
            <div className="tp-hint"><InlineSpinner /></div>
          )}
        </Step>

        <Step n="2" title="Review Your Statement" area="preview" last>
          <p className="tp-hint" style={{ marginBottom: 8 }}>Check the sessions, pax, amounts and bank details. You can't change them after you submit.</p>
          <div className="sgn-preview-bar">
            <span><i className="bi bi-file-earmark-pdf" aria-hidden="true" /> Claim Statement</span>
            <span className="tp-pill mute"><i className="bi bi-eye" aria-hidden="true" />Preview · Not Filed Yet</span>
          </div>
          <div className="sgn-paper">
            {previewError ? (
              <div className="tp-error" style={{ margin: 16 }}><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>{previewError}</span></div>
            ) : pdfUrl ? (
              <iframe title="Claim statement preview" src={`${pdfUrl}#view=FitH&toolbar=0&navpanes=0`} />
            ) : null}
            {previewing && <div className="sgn-loading"><InlineSpinner /><span>Updating preview…</span></div>}
          </div>
        </Step>

        <Step n="3" title="Add Your Signature" area="sign">
          {saved ? (
            <SignatureInput key={String(saved.has_signature)} hasSaved={saved.has_signature} savedImage={saved.image} onChange={setSignature} />
          ) : (
            <div className="tp-hint"><InlineSpinner /></div>
          )}
        </Step>

        <Step n="4" title="Confirm and Submit" area="confirm" last>
          <label className="tp-check" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
            <span>I confirm these details are correct and I'm signing this claim.</span>
          </label>
          {missing && !submitting && <div className="tp-hint" style={{ marginTop: 10 }}><i className="bi bi-info-circle" aria-hidden="true" /> {missing} to continue.</div>}
          <div className="tp-actions" style={{ marginTop: 10 }}>
            <button type="button" className="tp-btn" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="button" className="tp-btn primary" disabled={!canSubmit}
              onClick={() => onSubmit({ signature: signature || undefined, bank_name: bank.name.trim(), bank_account_number: account })}>
              {submitting ? <InlineSpinner /> : <><i className="bi bi-send" aria-hidden="true" />Sign and Submit</>}
            </button>
          </div>
        </Step>
      </div>
    </Modal>
  );
}
